// @ts-nocheck
import { useEffect, useState } from 'react';
import axios from 'axios';
import utils from '../../utils/utils';
import useConsole from '../Console/useConsole';
import useDatasetGrid from '../useDatasetGrid';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';

const MD_BASE_URL = ApiEndpoints.MODULE_BASE_URL.MD;
const ME_BASE_URL = ApiEndpoints.MODULE_BASE_URL.ME;

/**
 * @function useModelDeployment
 * @description Model Deployment custom hook
 * @param {function} fetchModuleTasks - fetching module tasks
 * @param {function} fetchDatasetList - fetching dataset list
 * @param {Array} datasetList - dataset list
 * @param {function} fetchDatasetRowsWithPagination - fetching dataset rows with pagination
 * @param {Array} datasetRowsWithPagination - dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - setting dataset rows with pagination
 * @param {number} totalDatasetRows - total dataset rows
 * @param {number} datasetRowsPageCount - dataset rows page count
 * @param {boolean} isLoading - loading state
 * @param {function} updateDatasetCells - updating dataset cells
 * @param {object} project - project object
 * @returns {object} all model deployment methods
 * @example useModelDeployment()
 */

export default function useModelDeployment() {
  const {
    fetchModuleTasks,
    fetchDatasetList,
    datasetList,
    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,
    updateDatasetCells,
    project,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    gridOperationRerun,

    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
  } = useDatasetGrid();

  const {
    consoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();
  const { markDatasetAsReadyToUse } = useDataIngestion();
  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();

  // For Active Operation
  const [activeOperation, setActiveOperation] = useState({});

  // For Active Dataset
  const [activeDataset, setActiveDataset] = useState('');

  // For Column Details of Active Dataset
  // const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  // all models data
  const [allModelData, setAllModelData] = useState([]);

  // ME tasks for best model selection
  const [allMETasks, setAllMETasks] = useState([]);

  // Preliminary Steps Tasks
  const [moduleTasks, setModuleTasks] = useState([]);

  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);

  const fetchMEModuleTasks = (projectId, hideLoaderOnRequest) => {
    if (projectId) {
      fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.ME.subModules.ME.key,
        'GET_ME_TASKS',
        setAllMETasks,
        projectId,
        hideLoaderOnRequest
      );
    }
  };

  // For Response Data : Graphs or Tables or Table ID
  const [taskDetails, updateTaskDetails] = useState({});
  /**
   * @function resetTaskDetails
   * @description reseting of task details
   * @returns {void}
   * @example resetTaskDetails()
   */
  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  // // For fetching Active Dataset Columns
  // /**
  //  * @function fetchDatasetColumnDetails
  //  * @description API call for fetching dataset column details
  //  * @param {string} datasetName - name of the dataset
  //  * @returns {Promise} AxiosPromise
  //  * @example  fetchDatasetColumnDetails(datasetName)
  //  */
  // const fetchDatasetColumnDetails = (datasetName) =>
  //   axios
  //     .post(
  //       `${MD_BASE_URL + ApiEndpoints.MD.DATASET_COLUMN_DETAILS}`,
  //       datasetName
  //     )
  //     .then(
  //       (res) => {
  //         setDatasetColumnDetails(res.data?.columns);
  //       },
  //       () => {}
  //     );

  // For Fetaching the Tasks List
  /**
   * @function fetchMDModuleTasks
   * @description fetching feature engineering module tasks
   * @param {number} projectId - project id
   * @param {boolean} hideLoaderOnRequest - hiding loader on request
   * @returns {void}
   * @example  fetchMDModuleTasks(projectId, hideLoaderOnRequest)
   */
  const fetchMDModuleTasks = (projectId, hideLoaderOnRequest) => {
    if (projectId) {
      fetchModuleTasks(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MD.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MD.subModules.MD.key,
        'GET_MD_TASKS',
        setModuleTasks,
        projectId,
        hideLoaderOnRequest
      );
    }
  };

  /**
   * @function fetchMDModuleTasksConsole
   * @description API call for fetching data quality module tasks
   * @param {number} projectId - module tasks of a particular project id
   * @param {boolean} hideLoaderOnRequest -hiding loader on request
   * @returns {void}
   * @example fetchMDModuleTasksConsole(project_id,hideLoaderOnRequest);
   */
  const fetchMDModuleTasksConsole = (projectId, hideLoaderOnRequest) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.MD.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.MD.key,
        'GET_MD_TASKS',
        projectId,
        hideLoaderOnRequest
      );
    }
  };

  // Fetch Asnyc Operation Result
  /**
   * @function fetchAsyncResult
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   *  @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResult(taskID, additionTaskDetails, successCallback)
   */
  const fetchAsyncResult = (taskID, additionTaskDetails, successCallback) =>
    axios
      .get(`${`${MD_BASE_URL + ApiEndpoints.MD.GET_MD_TASKS}/${taskID}`}`)
      .then(
        (res) => {
          if (res.data.task_status === 'SUCCESS') {
            Object.values(
              MODULE_SUB_MODULE_OPERATIONS_TREE.MD.subModules.MD.operations
            ).filter((data) => {
              if (
                data.key === additionTaskDetails.operation &&
                res.data?.parameters?.dataset_id_one
              ) {
                datasetList.forEach((dataset) => {
                  if (
                    dataset.id === Number(res.data.parameters.dataset_id_one)
                  ) {
                    setActiveDataset(dataset);
                  }
                });

                updateTaskDetails(res.data);
                setActiveOperation(data);
                successCallback(res.data);
              }
              return null;
            });
          } else {
            updateTaskDetails({});
          }
        },
        () => {}
      );

  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.Name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function fetchAllModelDetails
   * @description API call to get all models details associated with the active project.
   * @param {number} projectId - projectId of the active project.
   * @param {string} field - fields to be fetched
   * @returns {Promise} AxiosPromise
   * @example fetchAllModelDetails(projectId);
   */
  // For fetching list of all models
  const fetchAllModelDetails = (projectId, field) => {
    if (projectId) {
      axios
        .get(`${ME_BASE_URL}${ApiEndpoints.MI.MT_SS_ALL_MODELS_DETAILS}`, {
          params: {
            project_id: projectId,
            ...(field ? { fields: field } : {}),
          },
        })
        .then(
          (res) => {
            setAllModelData(res.data);
            return res.data;
          },
          () => {}
        );
    }
    // no return statement
  };

  // For Terminating Task in Task Manager
  /**
* @function  terminateTask
* @description API call for terminate  task
* @param {number}  taskID - task id
*  @param {function} successCallback - Callback function to execute upon completion of API call
*  @param {function} errorCallback - Callback function to execute when any error
happens during the calling of API
* @returns  {Promise} AxiosPromise
* @example  terminateTask(taskID, successCallback, errorCallback)
*/
  const terminateTask = (taskID) =>
    axios
      .delete(`${`${MD_BASE_URL + ApiEndpoints.MD.GET_MD_TASKS}/${taskID}`}`)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });

  /**
   * @function mdRerunOperation
   * @description API call for reruning the operation
   * @param {object} body - body
   * @returns {Promise} AxiosPromise
   * @example mdRerunOperation(body);
   */
  const mdRerunOperation = (body) => {
    return axios
      .post(`${MD_BASE_URL + ApiEndpoints.MD.MD_RERUN}`, body)
      .then((res) => res)
      .catch((e) => {
        throw e;
      });
  };

  const submitDataDrift = (body) =>
    axios.post(`${MD_BASE_URL + ApiEndpoints.MD.MD_DATA_DRIFT}`, body).then(
      (res) => res,
      (error) => {
        throw error;
      }
    );

  const bestModelSelectionSubmit = (body) =>
    axios
      .post(`${MD_BASE_URL + ApiEndpoints.MD.MD_BEST_MODEL_SELECTION}`, body)
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );

  return {
    activeOperation,
    setActiveOperation,

    activeDataset,
    setActiveDataset,

    // datasetColumnDetails,
    // fetchDatasetColumnDetails,

    moduleTasks,
    fetchMDModuleTasks,
    fetchAllModelDetails,
    allModelData,

    submitDataDrift,
    bestModelSelectionSubmit,

    fetchMEModuleTasks,
    allMETasks,

    taskDetails,
    resetTaskDetails,
    fetchAsyncResult,

    datasetList,
    terminateTask,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,

    renameColumn,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    fetchGridTasks,
    terminateGridTask,
    fetchDatasetList,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    gridOperationRerun,

    analyse,
    mdRerunOperation,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    // markTaskReadUnreadModelDeployment,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,

    // console related
    consoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchMDModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    project,
    setConsoleAsyncTaskResults,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    markDatasetAsReadyToUse,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
    fetchConsoleFileList,
  };
}
